import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Divider,
  Drawer,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

const fieldSx = {
  "& .MuiInputBase-root": {
    minHeight: 38,
    borderRadius: 0,
    fontSize: "0.72rem",
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.72rem",
  },
  "& .MuiFormHelperText-root": {
    mx: 0,
    fontSize: "0.62rem",
  },
};

export function AnalyticsFiltersDrawer({
  open,
  onClose,
  filters,
  setFilters,
  shops,
  currencies,
  onApply,
  onReset,
}) {
  const selectedShops = shops.filter((shop) =>
    filters.shop_ids.includes(shop.id),
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "92vw", sm: 340 },
          maxWidth: "100%",
          p: 0,
        },
      }}
    >
      <Box sx={{ p: 1.7 }}>
        <Typography sx={{ fontSize: "0.88rem", fontWeight: 850 }}>
          Filters
        </Typography>
        <Typography sx={{ mt: 0.25, color: "#667085", fontSize: "0.64rem" }}>
          Narrow the authorised reporting scope.
        </Typography>
      </Box>

      <Divider />

      <Stack spacing={1.25} sx={{ p: 1.7, overflowY: "auto", flex: 1 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 0.9,
          }}
        >
          <TextField
            label="From"
            type="date"
            value={filters.from_date || ""}
            onChange={(event) =>
              setFilters((current) => ({
                ...current,
                from_date: event.target.value,
              }))
            }
            InputLabelProps={{ shrink: true }}
            fullWidth
            sx={fieldSx}
          />

          <TextField
            label="To"
            type="date"
            value={filters.to_date || ""}
            onChange={(event) =>
              setFilters((current) => ({
                ...current,
                to_date: event.target.value,
              }))
            }
            InputLabelProps={{ shrink: true }}
            fullWidth
            sx={fieldSx}
          />
        </Box>

        <Autocomplete
          multiple
          options={shops}
          value={selectedShops}
          disableCloseOnSelect
          limitTags={1}
          isOptionEqualToValue={(option, selected) => option.id === selected.id}
          getOptionLabel={(option) =>
            `${option.shop_name} (${option.shop_code})`
          }
          onChange={(_, selected) =>
            setFilters((current) => ({
              ...current,
              shop_ids: selected.map((shop) => shop.id),
            }))
          }
          renderOption={(props, option, { selected }) => (
            <li {...props} key={option.id}>
              <Checkbox checked={selected} size="small" />
              <ListItemText
                primary={option.shop_name}
                secondary={option.shop_code}
                primaryTypographyProps={{ fontSize: "0.69rem", fontWeight: 700 }}
                secondaryTypographyProps={{ fontSize: "0.61rem" }}
              />
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Shops"
              helperText={`${filters.shop_ids.length} of ${shops.length} selected`}
              sx={fieldSx}
            />
          )}
        />

        <FormControl fullWidth sx={fieldSx}>
          <InputLabel id="currencies-label">Currencies</InputLabel>
          <Select
            labelId="currencies-label"
            label="Currencies"
            multiple
            value={filters.currencies}
            onChange={(event) =>
              setFilters((current) => ({
                ...current,
                currencies:
                  typeof event.target.value === "string"
                    ? event.target.value.split(",")
                    : event.target.value,
              }))
            }
            renderValue={(selected) => selected.join(", ")}
          >
            {currencies.map((currency) => {
              const code = currency.code || currency;
              return (
                <MenuItem key={code} value={code}>
                  <Checkbox checked={filters.currencies.includes(code)} />
                  <ListItemText
                    primary={code}
                    secondary={currency.name || ""}
                    primaryTypographyProps={{ fontSize: "0.69rem", fontWeight: 700 }}
                    secondaryTypographyProps={{ fontSize: "0.61rem" }}
                  />
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        <TextField
          select
          label="Group by"
          value={filters.group_by}
          onChange={(event) =>
            setFilters((current) => ({
              ...current,
              group_by: event.target.value,
            }))
          }
          fullWidth
          sx={fieldSx}
        >
          <MenuItem value="day">Day</MenuItem>
          <MenuItem value="week">Week</MenuItem>
          <MenuItem value="month">Month</MenuItem>
        </TextField>

        {Object.prototype.hasOwnProperty.call(filters, "shop_ranking") ? (
          <TextField
            select
            label="Shop ranking"
            value={filters.shop_ranking}
            onChange={(event) =>
              setFilters((current) => ({
                ...current,
                shop_ranking: event.target.value,
              }))
            }
            fullWidth
            sx={fieldSx}
          >
            <MenuItem value="top">Top 10 shops</MenuItem>
            <MenuItem value="bottom">Bottom 10 shops</MenuItem>
          </TextField>
        ) : null}
      </Stack>

      <Divider />

      <Stack direction="row" spacing={0.8} sx={{ p: 1.5 }}>
        <Button
          color="inherit"
          variant="outlined"
          fullWidth
          onClick={onReset}
          sx={{ borderRadius: 0, fontSize: "0.68rem" }}
        >
          Reset
        </Button>
        <Button
          variant="contained"
          fullWidth
          onClick={() => {
            onApply();
            onClose();
          }}
          sx={{ borderRadius: 0, fontSize: "0.68rem" }}
        >
          Apply
        </Button>
      </Stack>
    </Drawer>
  );
}
